<template>
  <div class="home-container" :class="{ fullscreen }" v-resize="onResize">
    <PWA v-if="$route.query.cache !== 'false'" />

    <div id="map-container" :class="{ fullscreen }" ref="mobile-card-bounds">
      <MapContainer
        ref="map"
        v-if="locations.length > 0 && campuses.length > 0"
        @ready="onMapReady()"
        @control-click="onControlClick"
      />
    </div>

    <!-- Fullscreen overlays -->
    <transition name="fade-overlay">
      <Overlay
        fullscreen
        :padded="false"
        v-if="$route.params.id && $route.params.mode === 'events'"
        @close="$router.push({ name: 'location', params: { id: $route.params.id, mode: undefined } })">
        <Events />
      </Overlay>
    </transition>

    <transition name="fade-overlay">
      <Overlay
        fullscreen
        :padded="!$vuetify.breakpoint.smAndDown"
        v-if="$route.params.id && $route.params.mode === 'departments'"
        @close="$router.push({ name: 'location', params: { id: $route.params.id, mode: undefined } })">
        <Departments />
      </Overlay>
    </transition>

    <!-- Results Portal -->
    <portal :to="($vuetify.breakpoint.mdAndUp) ? 'results-outlet-desktop' : 'results-outlet-mobile'">
      <v-slide-y-transition>
        <Overlay
          heading="Results"
          :padded="false"
          v-if="$route.params.id && $route.name !== 'directions'"
          @close="onResultsClosed()">
          <Results />
        </Overlay>
      </v-slide-y-transition>

      <v-slide-y-transition>
        <Overlay
          heading="Directions"
          v-if="$route.name === 'directions'"
          @close="$router.push({ name: 'location', params: { id: $route.params.id } })">
          <Directions ref="directions" />
        </Overlay>
      </v-slide-y-transition>

      <v-slide-y-transition>
        <Overlay
          heading="Metro Transit"
          v-if="$route.name === 'transit' && transitStops.length"
          @close="$router.push({ name: 'location', params: { id: $route.params.id, mode: undefined } })">
          <Transit />
        </Overlay>
      </v-slide-y-transition>

      <v-slide-y-transition>
        <Overlay
          heading="Hours"
          v-if="$route.params.id && $route.params.mode === 'hours'"
          @close="$router.push({ name: 'location', params: { id: $route.params.id, mode: undefined } })">
          <Hours />
        </Overlay>
      </v-slide-y-transition>
    </portal>

    <!-- Left UI Container -->
    <v-expand-transition>
      <v-container
        class="ui-container py-0"
        v-show="!hidden.search || $vuetify.breakpoint.mdAndUp">
        <v-row class="ui-container-heading" v-if="!$vuetify.breakpoint.mdAndUp">
          <v-col cols="10">Search</v-col>
          <v-spacer />
          <v-col @click="hidden.search = true" class="text-right" >
            <i class="fa fa-times-circle close-pane" aria-hidden="true" />
          </v-col>
        </v-row>

        <div class="ui-container-content">
          <Overlay heading="Choose Campus & Building" :allow-close="false">
            <LocationSelector />
          </Overlay>
          <Overlay :allow-close="false" dark>
            <v-form @submit.prevent="validate()" ref="form">
              <v-text-field v-model="searchInput" label="Search..." append-icon="fa-search" clearable dark @click:append="validate()" @click:clear="onSearchCleared()" />
            </v-form>
          </Overlay>

          <portal-target
            v-if="$vuetify.breakpoint.mdAndUp"
            name="results-outlet-desktop" />
        </div>
      </v-container>
    </v-expand-transition>

    <!-- Right UI Container -->
    <v-expand-transition>
      <v-container class="ui-container right py-0" v-show="($vuetify.breakpoint.smAndDown && !hidden.locationFilter) || $vuetify.breakpoint.mdAndUp">
        <v-row class="ui-container-heading" v-if="!$vuetify.breakpoint.mdAndUp">
          <v-col cols="10">Filter</v-col>
          <v-spacer />
          <v-col @click="hidden.locationFilter = !hidden.locationFilter" class="text-right">
            <i class="fa fa-times-circle close-pane" aria-hidden="true" />
          </v-col>
        </v-row>

        <div class="ui-container-content">
          <Overlay :allow-close="false" dark>
            <LocationFilter dark />
          </Overlay>
          <v-combobox
            v-model="filteredTagsLocal"
            :items="tags"
            small-chips
            label="Filter by tag..."
            multiple
            solo
            deletable-chips
            :allow-overflow="false"
            hide-no-data
            hide-selected
            clearable
            class="v-combobox rounded-0"
          />
        </div>
      </v-container>
    </v-expand-transition>

    <!-- Mobile Results UI Container -->
    <div
      v-show="$vuetify.breakpoint.smAndDown && isResultsOpen"
      class="ui-container bottom"
      ref="mobile-card-target">
      <MobileCard
        ref="mobile-card"
        :handle-height.sync="mobileCardHandleHeight"
        :bounds="$refs['mobile-card-bounds']"
        :target="$refs['mobile-card-target']">
        <v-expand-transition>
          <v-container v-if="$vuetify.breakpoint.smAndDown && isResultsOpen" class="py-0">
            <div class="ui-container-content">
              <portal-target
                v-if="$vuetify.breakpoint.smAndDown"
                name="results-outlet-mobile" />
            </div>
          </v-container>
        </v-expand-transition>
      </MobileCard>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import search from '@/mixins/search'

import Overlay from '@/components/Overlay'
import MobileCard from '@/components/MobileCard'
import PWA from '@/components/PWA'
import MapContainer from '@/components/home/MapContainer'
import Directions from '@/components/home/Directions'
import LocationFilter from '@/components/home/LocationFilter'
import LocationSelector from '@/components/home/LocationSelector'
import Results from '@/components/home/Results'
import Departments from '@/components/home/Departments'
import Events from '@/components/home/Events'
import Transit from '@/components/home/Transit'
import Hours from '@/components/home/Hours'

export default {
  name: 'Home',
  components: {
    Overlay,
    MobileCard,
    MapContainer,
    Directions,
    LocationFilter,
    LocationSelector,
    Results,
    Departments,
    Events,
    Transit,
    Hours,
    PWA
  },
  mixins: [
    search
  ],
  computed: {
    ...mapGetters([
      'campuses',
      'transitStops',
      'activeCampus',
      'fullscreen',
      'activeLocations',
      'tags',
      'filteredTags'
    ]),
    isResultsOpen () {
      return ['location', 'directions', 'events', 'departments', 'transit'].includes(this.$route.name)
    },
    filteredTagsLocal: {
      get () {
        return this.filteredTags
      },
      set (input) {
        this.setFilteredTags(input)
      }
    }
  },
  data () {
    return {
      searchInput: '',
      searchInputHandle: null,
      hidden: {
        search: this.$vuetify.breakpoint.smAndDown,
        locationFilter: this.$vuetify.breakpoint.smAndDown
      },
      isMapReady: false,
      // Temporarily unused as it's not being updated correctly
      mobileCardHandleHeight: 36
    }
  },
  watch: {
    isResultsOpen: {
      handler: 'onIsResultsOpenChange'
    },
    activeLocations () {
      this.hidden.search = true
      this.hidden.locationFilter = true

      this.$refs['mobile-card'].reset()
    }
  },
  async mounted () {
    // On route search query set
    if (this.$route.query.search) {
      this.searchInput = this.$route.query.search
      await this.$nextTick()
      this.validate()
    }
  },
  methods: {
    ...mapActions([
      'setActiveLocations'
    ]),
    ...mapMutations([
      'setActiveCampus',
      'setSelectedLocation',
      'setFilteredTags'
    ]),
    async validate () {
      if (this.searchInput.length === 0) {
        this.$router.push({ query: Object.assign({}, this.$route.query, { search: undefined }) })
      } else {
        this.$router.push({ query: Object.assign({}, this.$route.query, { search: this.searchInput }) })
      }

      if (this.$refs.form.validate()) {
        const results = await this.search(this.searchInput)

        if (results.length > 0) {
          this.setActiveLocations(results)
        }

        if (results.length === 1) {
          this.setActiveCampus(results[0].campus)
          this.setSelectedLocation(results[0])
        }
      }
    },
    onResize () {
      const width = window.innerWidth

      // Extra Small
      if (width > 960) {
        this.hidden.locationFilter = false
        this.hidden.search = false
      } else {
        this.hidden.locationFilter = true
        this.hidden.search = true
      }
    },
    onSearchCleared () {
      this.$router.push({ query: Object.assign({}, this.$route.query, { search: undefined }) })
    },
    onResultsClosed () {
      this.$router.push({ name: 'location', params: { id: undefined } })
      this.setSelectedLocation(null)
      this.setActiveLocations([])
    },
    onControlClick (event) {
      switch (event) {
        case 'directions':
          this.$router.push({ name: 'directions', params: { id: location._id } })
          break
        case 'search':
          this.hidden.search = !this.hidden.search
          break
        case 'filter':
          this.hidden.locationFilter = !this.hidden.locationFilter
          break
      }
    },
    async onIsResultsOpenChange () {
      await this.$nextTick()

      const bottomControls = document.getElementsByClassName('leaflet-bottom')

      for (const control of bottomControls) {
        if (this.isResultsOpen && this.$vuetify.breakpoint.smAndDown) {
          control.style.transform = 'translateY(-36px)'
        } else {
          control.style.transform = 'translateY(-0px)'
        }
      }
    },
    onMapReady () {
      this.isMapReady = true
      this.onIsResultsOpenChange()
    }
  }
}
</script>

<style lang="scss">
.home-container {
  position: relative;
  height: 75vh;

  &.fullscreen {
    height: 100%;
  }
}

#map-container {
  position: relative;
  height: 75vh;

  &.fullscreen {
    height: 100%;
    position: fixed;
  }
}

.ui-container {
  position: absolute;
  top: 0;
  width: 100vw;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  min-width: 20%;

  &-heading {
    background-color: var(--v-accent-base);
    color: #FFFFFF;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  &-content {
    margin-left: -12px;
    margin-right: -12px
  }

  &.bottom {
    bottom: 0;
    top: auto;
  }
}

.v-combobox {
  .v-input__slot {
    box-shadow: 0px 2px 4px rgba(0,0,0,.5) !important;
  }
}

@media only screen and (min-width: 960px) {
  .ui-container {
    top: 0;
    left: 50px;
    position: absolute;
    width: auto !important;
    bottom: auto;
    margin-bottom: 8px;

    &-content {
      padding-top: 11px;
      padding-right: 5px;
      padding-left: 5px;
    }

    &.right {
      right: 0;
      left: auto;
      padding-right: 16px;

      &-content {
        padding-left: 5px;
      }
    }
  }

  // Including #app to fix a CSS load order bug
  #app {
    .v-combobox {
      max-width: 25vw
    }
  }
}
</style>
